import React from 'react';
import _startCase from 'lodash/startCase';
import _get from 'lodash/get';
import { useQuery } from '@apollo/client';
import { Cell, Row, ColumnInterface } from 'react-table';

import { queryStrings } from '@trqk/resolvers';

import { defaultColumnWidth } from '../../../constants';

/* eslint-disable @typescript-eslint/ban-types -- the type defs for react-table use the "object"
   type extensively, so we can't avoid using it when we extend them. */
interface TooltipColumn<D extends object> extends ColumnInterface<D> {
    tooltipPosition: TooltipPosition;
}

interface Props {
    cell: Cell<ActiveEntity>;
    column: TooltipColumn<ActiveEntity>;
    row: Row<ActiveEntity>;
}

const { GET_UNVIEWED_STATEMENTS } = queryStrings;
const countWidth = 30;
const dotWidth = 14;

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Uses a tooltip and CSS ellipses to truncate text when necessary.
 * Also converts text to start case with lodash/startCase.
 *
 * This is mostly copied from: packages/components/src/table/cell/TruncatedNameCell.tsx, with the
 * addition of:
 * - rendering a teal dot if the entity has at least one statement that was ingested ten minutes or
 * less before the last time ManageStatements was opened
 * - rendering the count of currently selected statements for this entity if the count is over zero
 */
export function EntityNameCell({ cell, column, row }: Props): JSX.Element {
    const {
        value: {
            entity: { name },
            completedStatementViews,
        },
    } = cell;
    const text = _startCase(name);
    const selectedStatementIdsLength = _get(
        row,
        ['state', 'selectedStatementIds', 'length'],
        0,
    );
    const width = (column?.width as number) ?? defaultColumnWidth;
    const selectedStatementsWidth =
        selectedStatementIdsLength > 0 ? countWidth : 0;
    const unviewedStatementsQuery = useQuery(GET_UNVIEWED_STATEMENTS);
    const unviewedStatements: string[] =
        unviewedStatementsQuery?.data?.uiState?.statements
            ?.unviewedStatements ?? [];
    const hasNewIngestion = completedStatementViews.some(
        ({ statement_id }: TrqkStatementView): boolean =>
            statement_id !== null &&
            statement_id !== undefined &&
            unviewedStatements.includes(statement_id),
    );
    const newIngestionWidth = hasNewIngestion ? dotWidth : 0;
    const truncateWidth = width - newIngestionWidth - selectedStatementsWidth;

    return (
        <div className="flex justify-start pr-2 items-center font-medium">
            <div
                {...row.getToggleRowExpandedProps()}
                className="respond-to-mouse truncate cursor-pointer"
                style={{
                    maxWidth: truncateWidth,
                }}
            >
                {text}
            </div>
            {hasNewIngestion && (
                <div
                    className="mx-1 bg-secondary-500 rounded-full"
                    style={{ width: 6, height: 6 }}
                />
            )}
            {selectedStatementIdsLength > 0 && (
                <div
                    className="ml-1 text-quinary-500"
                    style={{ maxWidth: countWidth }}
                >
                    {`(${selectedStatementIdsLength})`}
                </div>
            )}
        </div>
    );
}
